import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CoverPage } from "@/components/CoverPage";
import { IntroductionPage } from "@/components/IntroductionPage";
import { RamadanIntroPage } from "@/components/RamadanIntroPage";
import { AuthPage } from "@/components/auth/AuthPage";
import { RamadanOnboarding } from "@/components/onboarding/RamadanOnboarding";
import { UnifiedDashboard } from "@/components/dashboard/UnifiedDashboard";
import { PeriodOverview } from "@/components/PeriodOverview";
import { DailyDuaPage } from "@/components/DailyDuaPage";
import { PreviewDashboard } from "@/components/dashboard/PreviewDashboard";
import { LaylatulQadrGuide } from "@/components/LaylatulQadrGuide";
import { NewsletterArchive } from "@/components/NewsletterArchive";
import { JournalPage } from "@/components/JournalPage";
import { PrivacyPolicy } from "@/components/PrivacyPolicy";
import { TermsOfService } from "@/components/TermsOfService";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useTrackerData, TrackerData } from "@/hooks/useTrackerData";

type PageView = "cover" | "preview" | "introduction" | "ramadan-intro" | "auth" | "onboarding" | "dashboard" | "periods" | "daily" | "trackers" | "journal" | "calendar" | "laylatul-qadr" | "newsletter" | "privacy" | "terms";

// Views that should be restored on refresh when the user has a session
const RESTORABLE_VIEWS = new Set<PageView>([
  "dashboard", "periods", "daily", "trackers", "journal", "calendar", "laylatul-qadr"
]);

const Index = () => {
  const { user, isGuest, isLoading, signOut } = useAuth();
  const { profile, isLoading: isProfileLoading } = useProfile();
  const { trackerData, updateTrackerData, saveDayData, defaultTrackerData } = useTrackerData();
  
  const [currentView, setCurrentViewState] = useState<PageView>(() => {
    const saved = localStorage.getItem("layaliCurrentView") as PageView | null;
    return saved && RESTORABLE_VIEWS.has(saved) ? saved : "cover";
  });
  const setCurrentView = (view: PageView) => {
    setCurrentViewState(view);
  };
  const [hasRestoredView, setHasRestoredView] = useState(false);
  const [isResumeFlow, setIsResumeFlow] = useState(false);
  const [currentDay, setCurrentDay] = useState(() => {
    const saved = localStorage.getItem("layaliCurrentDay");
    return saved ? Number(saved) : 1;
  });
  const [completedDays, setCompletedDays] = useState<number[]>([]);

  // Persist current view to localStorage
  useEffect(() => {
    if (RESTORABLE_VIEWS.has(currentView)) {
      localStorage.setItem("layaliCurrentView", currentView);
      localStorage.setItem("layaliCurrentDay", String(currentDay));
    }
  }, [currentView, currentDay]);

  // After auth resolves, validate or reset the restored view
  useEffect(() => {
    if (isLoading || hasRestoredView) return;
    setHasRestoredView(true);

    const hasSession = !!(user || isGuest);
    if (!hasSession) {
      // No session — force back to cover regardless of localStorage
      if (RESTORABLE_VIEWS.has(currentView)) {
        setCurrentView("cover");
      }
      return;
    }

    // Session exists — if we're on cover but have a saved view, restore it
    if (currentView === "cover") {
      if (localStorage.getItem("ramadanStartDate")) {
        setCurrentView("dashboard");
      }
    }
  }, [isLoading, user, isGuest, hasRestoredView]);

  // Load completed days from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("ramadanCompletedDays");
    if (saved) {
      setCompletedDays(JSON.parse(saved));
    }
  }, []);

  // Save completed days
  useEffect(() => {
    localStorage.setItem("ramadanCompletedDays", JSON.stringify(completedDays));
  }, [completedDays]);

  const handleStart = () => setCurrentView("introduction");
  const handleIntroNext = () => setCurrentView("ramadan-intro");
  const handleRamadanIntroNext = () => setCurrentView("auth");
  const handleAuthSuccess = () => {
    // Returning user signed in via Resume → go straight to dashboard
    setCurrentView("dashboard");
  };
  const handleResume = () => {
    // localStorage already confirmed they're returning
    setIsResumeFlow(true);
    if (user || isGuest) {
      // Already authenticated — go straight to dashboard
      setCurrentView("dashboard");
    } else {
      // Need to sign in first, then auth success goes to dashboard
      setCurrentView("auth");
    }
  };
  const handleOnboardingComplete = () => setCurrentView("dashboard");
  const handleBackToCover = () => setCurrentView("cover");

  const handleNavigate = (view: string, day?: number) => {
    if (day !== undefined) setCurrentDay(day);
    setCurrentView(view as PageView);
  };

  const handleLogout = async () => {
    await signOut();
    localStorage.removeItem("layaliCurrentView");
    localStorage.removeItem("layaliCurrentDay");
    setCurrentView("cover");
  };

  const handleSelectPeriod = (startDay: number) => {
    setCurrentDay(startDay);
    setCurrentView("daily");
  };

  const handleDayChange = (day: number) => setCurrentDay(day);

  const handleDayToggle = (day: number) => {
    setCompletedDays((prev) => 
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const handleTrackerUpdate = (updates: Partial<TrackerData>) => {
    const newData = { ...trackerData, ...updates };
    updateTrackerData(updates);
    saveDayData(currentDay, newData);
  };

  const handleOpenLaylatulQadr = () => setCurrentView("laylatul-qadr");
  const handleCloseLaylatulQadr = () => setCurrentView("daily");
  const handleOpenNewsletter = () => setCurrentView("newsletter");
  const handleCloseNewsletter = () => setCurrentView("cover");

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <div className="text-center">
          <div className="text-4xl mb-4" style={{ color: 'hsl(38, 40%, 55%)' }}>☽</div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <main>
    <AnimatePresence mode="wait">
      {currentView === "cover" && (
        <motion.div key="cover" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <CoverPage onStart={handleStart} onResume={handleResume} onPreview={() => setCurrentView("preview")} onOpenNewsletter={handleOpenNewsletter} onOpenPrivacy={() => setCurrentView("privacy")} onOpenTerms={() => setCurrentView("terms")} />
      </motion.div>
      )}
      {currentView === "preview" && (
        <motion.div key="preview" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <PreviewDashboard onClose={() => setCurrentView("cover")} onSignUp={handleStart} />
        </motion.div>
      )}
      {currentView === "introduction" && (
        <motion.div key="intro" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <IntroductionPage onNext={handleIntroNext} onBackToCover={handleBackToCover} />
        </motion.div>
      )}
      {currentView === "ramadan-intro" && (
        <motion.div key="ramadan-intro" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <RamadanIntroPage onNext={handleRamadanIntroNext} onBack={() => setCurrentView("introduction")} />
        </motion.div>
      )}
      {currentView === "auth" && (
        <motion.div key="auth" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <AuthPage onBack={() => setCurrentView(isResumeFlow ? "cover" : "ramadan-intro")} onSuccess={handleAuthSuccess} onOpenPrivacy={() => setCurrentView("privacy")} onOpenTerms={() => setCurrentView("terms")} />
        </motion.div>
      )}
      {currentView === "onboarding" && (
        <motion.div key="onboarding" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <RamadanOnboarding onComplete={handleOnboardingComplete} />
        </motion.div>
      )}
      {currentView === "dashboard" && (
        <motion.div key="dashboard" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <UnifiedDashboard
            currentDay={currentDay}
            completedDays={completedDays}
            trackerData={trackerData}
            onDayChange={handleDayChange}
            onTrackerUpdate={handleTrackerUpdate}
            onNavigate={handleNavigate}
            onLogout={handleLogout}
          />
        </motion.div>
      )}
      {currentView === "periods" && (
        <motion.div key="periods" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <PeriodOverview onSelectPeriod={handleSelectPeriod} onBackToCover={() => setCurrentView("dashboard")} completedDays={completedDays} />
        </motion.div>
      )}
      {(currentView === "daily" || currentView === "trackers" || currentView === "calendar") && (
        <motion.div key="daily" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <DailyDuaPage
            currentDay={currentDay}
            completedDays={completedDays}
            trackerData={trackerData}
            onDayChange={handleDayChange}
            onDayToggle={handleDayToggle}
            onTrackerUpdate={handleTrackerUpdate}
            onBackToCover={() => setCurrentView("dashboard")}
            onOpenLaylatulQadr={handleOpenLaylatulQadr}
          />
        </motion.div>
      )}
      {currentView === "journal" && (
        <motion.div key="journal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <JournalPage 
            onBack={() => setCurrentView("dashboard")} 
            onViewEntry={(date) => {
              setCurrentView("daily");
            }}
          />
        </motion.div>
      )}
      {currentView === "laylatul-qadr" && (
        <motion.div key="laylatul" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <LaylatulQadrGuide onClose={handleCloseLaylatulQadr} />
        </motion.div>
      )}
      {currentView === "newsletter" && (
        <motion.div key="newsletter" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <NewsletterArchive onBack={handleCloseNewsletter} />
        </motion.div>
      )}
      {currentView === "privacy" && (
        <motion.div key="privacy" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <PrivacyPolicy onBack={() => setCurrentView("cover")} />
        </motion.div>
      )}
      {currentView === "terms" && (
        <motion.div key="terms" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <TermsOfService onBack={() => setCurrentView("cover")} />
        </motion.div>
      )}
    </AnimatePresence>
    </main>
  );
};

export default Index;
